import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { CookiePreferences, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { map, Observable, take } from 'rxjs';
import { Profile } from 'src/app/auth/models';
import { getUser } from 'src/app/auth/store';
import { getAllRelatedStories, getAllRelatedInitiatives, getRelatedStoriesLoaded, getAllRelatedContent } from 'src/app/content/store';
import { Breadcrumb } from 'src/app/core/models';
import { getAllEvents } from 'src/app/events/store';
import { Initiative } from 'src/app/initiatives/models';
import { SetTheme } from 'src/app/store';
import { Story } from 'src/app/stories/models';
import { Event, EventDataDateSpan } from '../../models';
import { getSelectedEvent, LikeEvent } from '../../store';
import { BaseContent } from 'src/app/content/models';

@Component({
  selector: 'app-event-detail',
  templateUrl: './event-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventDetailComponent implements OnInit, OnDestroy {
  event$!: Observable<Event>;

  relatedStories$!: Observable<Story[]>;
  relatedInitiatives$!: Observable<Initiative[]>;
  relatedContent$!: Observable<BaseContent[]>;
  relatedEventsLoaded$!: Observable<number>;
  user$!: Observable<Profile | undefined>;
  cookiePreferences$?: Observable<CookiePreferences | null>;
  breadcrumbs: Breadcrumb[] = [];

  previewPlaceholder$!: Observable<string>;

  private dict = new FromDictionaryPipe(this.lang);

  constructor(private store: Store, private activatedRoute: ActivatedRoute, private lang: LanguageService) {
    this.store.dispatch(SetTheme({ theme: 'palette-dark' }));
  }

  ngOnInit(): void {
    this.event$ = this.store.select(getSelectedEvent);
    this.relatedStories$ = this.store.select(getAllRelatedStories);
    this.relatedInitiatives$ = this.store.select(getAllRelatedInitiatives);
    this.relatedEventsLoaded$ = this.store.select(getRelatedStoriesLoaded);
    this.cookiePreferences$ = this.store.select(getCookiePreferences);
    this.relatedContent$ = this.store.select(getAllRelatedContent);
    this.user$ = this.store.select(getUser);

    this.previewPlaceholder$ = this.user$.pipe(
      map(a => (a?.roles.includes('Administrator') ? 'evenement-niet-gepubliceerd-redactie' : 'evenement-niet-gepubliceerd')),
      map(a => this.dict.transform(a))
    );

    this.event$.pipe(take(1)).subscribe(a => {
      this.breadcrumbs = [
        {
          routerLink: ['/activiteiten'],
          title: this.dict.transform('breadcrumbs-evenementen-overzicht-label'),
        },
        ...(a.initiative
          ? [
              {
                routerLink: a.initiative!.url,
                title: a.initiative!.subtitle ?? a.initiative.title,
              },
            ]
          : []),
        {
          title: a.subtitle || a.title,
        },
      ];
    });
  }

  like(id: number) {
    this.store.dispatch(LikeEvent({ id }));
  }

  ngOnDestroy(): void {
    this.store.dispatch(SetTheme({ theme: 'palette-light' }));
  }

  getMonth(d: Date | string) {
    let date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleString('nl-NL', { month: 'short' });
  }

  notThisYear(d: Date | string) {
    let date = typeof d === 'string' ? new Date(d) : d;

    return date.getFullYear() !== new Date().getFullYear();
  }

  getCurrentAndOldEvents(dateSpans: EventDataDateSpan[]): { current: EventDataDateSpan[]; old: EventDataDateSpan[] } {
    const old = dateSpans.filter(a => new Date(a.start || '') <= new Date());
    const current = dateSpans.filter(a => new Date(a.start || '') > new Date());
    return { current, old };
  }
}
